// store/deliveryNotes.js — intranet-018
//
// Capa de datos de los albaranes (`/delivery-notes/*`). Sin estado: solo acciones que hacen la
// I/O con `this.$axios` y delegan TODO el mapeo/validación/clasificación de errores en
// `utils/deliveryNotes.js`. Todas las acciones rechazan con un error tipado
// (`isDeliveryNoteError`, `error.type`); nunca relanzan el error crudo de axios.
// El 401 global lo gestiona el interceptor de `plugins/axios.js`: aquí solo se tipa.

import {
	DELIVERY_NOTE_ERROR_TYPES as T,
	buildGenerateRequest,
	buildPreviewParams,
	classifyError,
	createDeliveryNoteError,
	mapDeliveryNote,
	mapPreviewResponse,
	parseGenerateResponse,
} from "@/utils/deliveryNotes.js";

// Las generaciones nuevas salen primero (`created_at DESC`): la recién creada está en la página 1.
const DETAIL_PAGE_ROWS = 50;
const PASSTHROUGH_DETAIL_ERRORS = [T.UNAUTHORIZED, T.NETWORK, T.CANCELLED];

function authHeaders() {
	const token = window.localStorage.getItem("auth._token.local");
	return token ? { Authorization: token } : {};
}

export default {
	namespaced: true,

	actions: {
		/**
		 * Vista previa por rango (`GET /delivery-notes/preview`, solo lectura).
		 * @param {Object} _ctx Contexto Vuex (sin usar).
		 * @param {{date_from: (string|Date), date_to: (string|Date), only_pending?: boolean}} payload
		 * @returns {Promise<{items: Array}>} Pedidos mapeados (ver `mapPreviewItem`).
		 */
		async previewDeliveryNotes(_ctx, payload) {
			const params = buildPreviewParams(payload);
			try {
				const response = await this.$axios.get("/delivery-notes/preview", { params, headers: authHeaders() });
				return mapPreviewResponse(response.data);
			} catch (error) {
				throw await classifyError(error);
			}
		},

		/**
		 * Genera el XML (`POST /delivery-notes/generate`). Sin reintento automático: un fallo tras
		 * enviar no se reenvía (podría duplicar lo importado en Timón).
		 * @param {Object} _ctx Contexto Vuex (sin usar).
		 * @param {{date_from: (string|Date), date_to: (string|Date), mode: string, purchase_order_ids?: number[]}} payload
		 * @returns {Promise<{blob: Blob, filename: string, deliveryNoteId: ?number, summary: ?Object}>}
		 */
		async generateDeliveryNotes(_ctx, payload) {
			const body = buildGenerateRequest(payload);
			let response;
			try {
				response = await this.$axios.post("/delivery-notes/generate", body, {
					responseType: "blob",
					headers: authHeaders(),
				});
			} catch (error) {
				throw await classifyError(error);
			}
			return parseGenerateResponse(response);
		},

		/**
		 * Detalle de una generación (exclusiones, `ignored_ids`, `already_generated`). No existe
		 * `GET /delivery-notes/:id`: se busca por `ID` en la primera página del listado.
		 * @param {Object} _ctx Contexto Vuex (sin usar).
		 * @param {number} id `delivery_note_id` de la generación.
		 * @returns {Promise<{id: number, exclusions: Array, ignoredIds: Array, alreadyGenerated: number[]}>}
		 */
		async fetchDeliveryNote(_ctx, id) {
			if (!Number.isInteger(id) || id <= 0) {
				throw createDeliveryNoteError(T.VALIDATION, { message: "Identificador de generación no válido." });
			}
			let response;
			try {
				response = await this.$axios.get("/delivery-notes/1", {
					params: { rows: DETAIL_PAGE_ROWS },
					headers: authHeaders(),
				});
			} catch (error) {
				const typed = await classifyError(error);
				if (PASSTHROUGH_DETAIL_ERRORS.includes(typed.type)) throw typed;
				throw createDeliveryNoteError(T.DETAIL_UNAVAILABLE, { status: typed.status });
			}

			const items = response.data && Array.isArray(response.data.items) ? response.data.items : [];
			const note = items.find((item) => item && item.ID === id);
			if (!note) throw createDeliveryNoteError(T.DETAIL_UNAVAILABLE);
			return mapDeliveryNote(note);
		},
	},
};
